/**
 * `EventCachePort` implementation over Dexie/IndexedDB (ADR-0001 `src/data`
 * ownership, ADR-0004 client queue).
 *
 * Invariant this module must uphold (ports.ts doc comment): `appendLocal`
 * makes cache-store and outbox-enqueue atomic -- an event is never visible
 * locally without being queued, and vice versa. Dexie's `transaction('rw',
 * ...)` gives this for free across the two stores.
 */

import type { CompassEvent, EventCachePort, EventId, SyncCursor } from '../../contracts';
import { INITIAL_SYNC_CURSOR } from '../../contracts';
import { sortByEventId } from '../../domain/event-log/event-log';
import { CompassDb, META_KEY_CURSOR, META_KEY_QUARANTINE } from './compass-db';

export function createDexieEventCache(db: CompassDb = new CompassDb()): EventCachePort {
  async function appendLocal(events: readonly CompassEvent[]): Promise<void> {
    if (events.length === 0) return;
    await db.transaction('rw', db.events, db.outbox, async () => {
      for (const event of events) {
        // `put` (not `add`) so a duplicate append -- e.g. the same command
        // retried locally -- is idempotent rather than throwing on a
        // duplicate key.
        await db.events.put({ eventId: event.eventId, event });
        await db.outbox.add({ eventId: event.eventId });
      }
    });
  }

  async function allEvents(): Promise<readonly CompassEvent[]> {
    const rows = await db.events.toArray();
    return sortByEventId(rows.map((row) => row.event));
  }

  async function integrateRemote(events: readonly CompassEvent[]): Promise<void> {
    if (events.length === 0) return;
    await db.transaction('rw', db.events, async () => {
      for (const event of events) {
        // Dedupe by eventId: `put` overwrites an existing row with the same
        // ID, which is a no-op for immutable events (the payload for a given
        // ID never differs, ADR-0002) and inserts new ones. Never enqueued
        // to the outbox -- these events are already known to the server.
        await db.events.put({ eventId: event.eventId, event });
      }
    });
  }

  async function peekQueue(max: number): Promise<readonly CompassEvent[]> {
    const outboxRows = await db.outbox.orderBy('seq').limit(max).toArray();
    if (outboxRows.length === 0) return [];
    const eventIds = outboxRows.map((row) => row.eventId);
    const eventRows = await db.events.bulkGet(eventIds);
    const events: CompassEvent[] = [];
    for (const row of eventRows) {
      if (row) events.push(row.event);
    }
    return events;
  }

  async function markPushed(eventIds: readonly EventId[]): Promise<void> {
    if (eventIds.length === 0) return;
    const idSet = new Set<EventId>(eventIds);
    await db.transaction('rw', db.outbox, async () => {
      const rows = await db.outbox.where('eventId').anyOf([...idSet]).toArray();
      const seqs = rows.map((row) => row.seq).filter((seq): seq is number => seq !== undefined);
      await db.outbox.bulkDelete(seqs);
    });
  }

  async function quarantine(eventId: EventId): Promise<void> {
    await db.transaction('rw', db.outbox, db.meta, async () => {
      const rows = await db.outbox.where('eventId').equals(eventId).toArray();
      const seqs = rows.map((row) => row.seq).filter((seq): seq is number => seq !== undefined);
      await db.outbox.bulkDelete(seqs);

      const existing = await db.meta.get(META_KEY_QUARANTINE);
      const list = new Set<string>((existing?.value as string[] | undefined) ?? []);
      list.add(eventId);
      await db.meta.put({ key: META_KEY_QUARANTINE, value: [...list] });
    });
  }

  async function pendingCount(): Promise<number> {
    return db.outbox.count();
  }

  async function getCursor(): Promise<SyncCursor> {
    const row = await db.meta.get(META_KEY_CURSOR);
    return (row?.value as SyncCursor | undefined) ?? INITIAL_SYNC_CURSOR;
  }

  async function setCursor(cursor: SyncCursor): Promise<void> {
    await db.meta.put({ key: META_KEY_CURSOR, value: cursor });
  }

  /**
   * The only deletion path in the system (ports.ts doc comment) -- erases
   * `events` and `outbox` in full and resets the stored cursor to
   * `INITIAL_SYNC_CURSOR`, atomically. Quarantine list is intentionally left
   * untouched: it is diagnostic metadata about past poison events, not part
   * of "the log," and clearing it here would silently hide a real prior
   * error from `SyncStatusView`-adjacent tooling.
   */
  async function clearLocal(): Promise<void> {
    await db.transaction('rw', db.events, db.outbox, db.meta, async () => {
      await db.events.clear();
      await db.outbox.clear();
      await db.meta.put({ key: META_KEY_CURSOR, value: INITIAL_SYNC_CURSOR });
    });
  }

  return {
    appendLocal,
    allEvents,
    integrateRemote,
    peekQueue,
    markPushed,
    quarantine,
    pendingCount,
    getCursor,
    setCursor,
    clearLocal,
  };
}

/** Quarantined event IDs, for `SyncStatusView`-adjacent diagnostics/tests. */
export async function getQuarantinedEventIds(db: CompassDb): Promise<readonly EventId[]> {
  const row = await db.meta.get(META_KEY_QUARANTINE);
  const ids = (row?.value as string[] | undefined) ?? [];
  return ids as unknown as readonly EventId[];
}
